import { type ReactNode } from 'react';

import { Badge, Box, Button, Flex, Text } from '@vimeo/bokeh';
import { Checkmark } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { buildCheckoutUrl, type PaywallTrackingProps } from '@vimeo/picox';
import type { SubscriptionPlans } from '@vimeo/subscription-api-plans-data-hook';

import type { CardEntitlement } from '../ComparisonPaywall/comparisonFeatures';

interface ComparisonPlanCardProps {
  planName: string;
  description: string;
  price: string;
  billingInfo?: ReactNode;
  seatCount: string;
  seatIcon: ReactNode;
  storageAmount: string;
  keyFeaturesLabel: string;
  entitlements: CardEntitlement[];
  isCurrentPlan?: boolean;
  isRecommended?: boolean;
  compact?: boolean;
  fillHeight?: boolean;
  isDisabled?: boolean;
  paywallTracking?: PaywallTrackingProps;
  subscriptionTier?: Partial<SubscriptionPlans['tier']>;
  isTrial?: boolean;
}

export function ComparisonPlanCard({
  planName,
  description,
  price,
  billingInfo,
  seatCount,
  seatIcon,
  storageAmount,
  keyFeaturesLabel,
  entitlements,
  isCurrentPlan = false,
  isRecommended = false,
  compact = false,
  fillHeight = false,
  isDisabled = false,
  paywallTracking,
  subscriptionTier,
  isTrial = false,
}: ComparisonPlanCardProps) {
  const handleCtaClick = () => {
    if (typeof window === 'undefined' || !subscriptionTier || !paywallTracking) {
      return;
    }
    const checkoutPath = buildCheckoutUrl({
      ...paywallTracking,
      tier: subscriptionTier,
      isTrial,
    });
    window.location.assign(checkoutPath);
  };

  const ctaLabel = isTrial
    ? translate('Start free trial')
    : translate({
        singular: 'Join Vimeo {PLAN_NAME}',
        replacements: { PLAN_NAME: planName },
      });

  if (isRecommended) {
    return (
      <Flex
        direction="column"
        width="100%"
        height={fillHeight ? '100%' : undefined}
        bg="#875be5"
        borderWidth="2px"
        borderStyle="solid"
        borderColor="#875be5"
        borderRadius="2xl"
        overflow="hidden"
      >
        {/* Recommended banner — sits inside the purple wrapper */}
        <Flex alignItems="center" justifyContent="center" height="24px" px="24px">
          <Text fontSize="heading-2xs" fontWeight="medium" color="white" textAlign="center">
            {translate('Recommended')}
          </Text>
        </Flex>

        {/* Card content */}
        <Flex
          direction="column"
          gap="md"
          p="md"
          borderRadius="2xl"
          bg="surface"
          width="100%"
          flex="1"
        >
          {renderCardContent({
            planName,
            description,
            price,
            billingInfo,
            seatCount,
            seatIcon,
            storageAmount,
            keyFeaturesLabel,
            entitlements,
            isCurrentPlan: false,
            isRecommended: true,
            isDisabled,
            compact,
            ctaLabel,
            handleCtaClick,
          })}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      width="100%"
      height={fillHeight ? '100%' : undefined}
      position="relative"
    >
      <Flex
        direction="column"
        gap="md"
        p="md"
        borderWidth="2px"
        borderStyle="solid"
        borderColor="stroke"
        borderRadius="2xl"
        bg="surface"
        opacity={isCurrentPlan ? 0.7 : 1}
        width="100%"
        flex={fillHeight ? '1' : undefined}
      >
        {renderCardContent({
          planName,
          description,
          price,
          billingInfo,
          seatCount,
          seatIcon,
          storageAmount,
          keyFeaturesLabel,
          entitlements,
          isCurrentPlan,
          isRecommended: false,
          isDisabled,
          compact,
          ctaLabel,
          handleCtaClick,
        })}
      </Flex>
    </Flex>
  );
}

interface CardContentProps {
  planName: string;
  description: string;
  price: string;
  billingInfo?: ReactNode;
  seatCount: string;
  seatIcon: ReactNode;
  storageAmount: string;
  keyFeaturesLabel: string;
  entitlements: CardEntitlement[];
  isCurrentPlan: boolean;
  isRecommended: boolean;
  isDisabled: boolean;
  compact: boolean;
  ctaLabel: string;
  handleCtaClick: () => void;
}

function renderCardContent({
  planName,
  description,
  price,
  billingInfo,
  seatCount,
  seatIcon,
  storageAmount,
  keyFeaturesLabel,
  entitlements,
  isCurrentPlan,
  isRecommended,
  isDisabled,
  compact,
  ctaLabel,
  handleCtaClick,
}: CardContentProps) {
  return (
    <>
      {/* Plan name + badge */}
      <Flex direction="column" maxHeight={compact ? '62px' : undefined}>
        <Flex gap="sm" alignItems="center">
          <Text variant="heading-md" letterSpacing="-0.04em">
            {planName}
          </Text>
          {isCurrentPlan && (
            <Badge variant="secondary" size="sm">
              {translate('Your plan')}
            </Badge>
          )}
        </Flex>
        {!compact && (
          <Text fontSize="body-md" color="text-primary" lineHeight="1.2">
            {description}
          </Text>
        )}
      </Flex>

      {/* Price block */}
      <Flex direction="column" gap="xs" minHeight="61px">
        <Text variant="heading-xl" letterSpacing="-0.04em">
          {price}
        </Text>
        {!compact && billingInfo}
      </Flex>

      {compact ? null : (
        <>
          {/* CTA */}
          <Flex direction="column" gap="sm" pt="sm">
            {isRecommended ? (
              <Button
                size="md"
                variant="primary"
                width="100%"
                onClick={handleCtaClick}
                isDisabled={isDisabled}
              >
                {ctaLabel}
              </Button>
            ) : (
              <Box height="40px" opacity={0} />
            )}
          </Flex>

          {/* Spacer pushes entitlements to the bottom so both cards align */}
          <Box flex="1" />

          {/* Entitlements */}
          <Flex direction="column" gap="8px" pb="sm">
            <Flex gap="xs" alignItems="center">
              {seatIcon}
              <Text fontSize="body-md">{seatCount}</Text>
            </Flex>
            <Flex gap="xs" alignItems="start">
              <Box pt="2px" flexShrink={0}>
                <StorageIcon />
              </Box>
              <Text fontSize="body-md">{storageAmount}</Text>
            </Flex>
            <Text variant="heading-xs" lineHeight="18px">
              {keyFeaturesLabel}
            </Text>
            {entitlements.map((item, idx) => (
              <Flex
                key={idx}
                gap="xs"
                alignItems="center"
                visibility={item.text ? 'visible' : 'hidden'}
              >
                <Box
                  flexShrink={0}
                  width="16px"
                  height="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Checkmark width={16} height={16} />
                </Box>
                <Text fontSize="body-md" lineHeight="1.4">
                  {item.text || '\u00A0'}
                </Text>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </>
  );
}

function StorageIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 4.5C3 3.67157 5.23858 3 8 3C10.7614 3 13 3.67157 13 4.5C13 5.32843 10.7614 6 8 6C5.23858 6 3 5.32843 3 4.5Z"
        fill="currentColor"
      />
      <path
        d="M3 7C3 7 3 6.5 3 6C3 6 5.23858 7 8 7C10.7614 7 13 6 13 6C13 6.5 13 7 13 7C13 7.82843 10.7614 8.5 8 8.5C5.23858 8.5 3 7.82843 3 7Z"
        fill="currentColor"
      />
      <path
        d="M3 9.5C3 9.5 3 9 3 8.5C3 8.5 5.23858 9.5 8 9.5C10.7614 9.5 13 8.5 13 8.5C13 9 13 9.5 13 9.5C13 10.3284 10.7614 11 8 11C5.23858 11 3 10.3284 3 9.5Z"
        fill="currentColor"
      />
      <path
        d="M3 12C3 12 3 11.5 3 11C3 11 5.23858 12 8 12C10.7614 12 13 11 13 11C13 11.5 13 12 13 12C13 12.8284 10.7614 13.5 8 13.5C5.23858 13.5 3 12.8284 3 12Z"
        fill="currentColor"
      />
    </svg>
  );
}
